/**
 * @fileoverview A bed's jobs on its card (D56; owner, 2026-10-16: "warn on
 * the beds page"): the same job cards Today and the top of Plants draw
 * (JobCard), flat inside the bed's card, in the server's order, ticked the
 * same way (useTodoMarks).
 *
 * A job whose plants are in other beds too is drawn as this bed's part of it
 * (the server's view, todo.beds.list[].views; owner, 2026-10-16: "Water the
 * lettuce · 2 plants here", not "3 plants in 2 beds"): titled by this bed's
 * plants when its title names or counts them, its reason this bed's own (its
 * amount or days, its crops), only this bed's plants listed and counted with
 * this bed's note alone (no other bed's "run the soaker hose or drip"; the
 * audit, 2026-10-16), the card is ticked when they all are, and
 * ticking its circle ticks only them (a mark with this bed's plant ids). A
 * job that can only be ticked whole (no plant by plant) is ticked whole, as
 * on Today. Today and the top of Plants show the whole job.
 *
 * Which jobs are the bed's, and its part of each, are the server's; this
 * only draws them: the first few on the list's card, then "2 more jobs"
 * (opening the bed), all of them on the open bed.
 *
 * Where: LocationCard (its This week).
 * API: none of its own; marks through the page's useTodoMarks.
 * Words: the list's own (the brief's todo and todo.beds).
 *
 * @module BedJobs
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { JobCard } from '@/features/today/components/JobCard';
import { AnimatedList } from '@/shared/components/ui/AnimatedList';
import type { TodoMarks } from '@/features/today/lib/useTodoMarks';
import type { BriefBed, BriefTodo, TodoJob } from '@/shared/api/todayBrief';
import type { TodoMark } from '@/shared/api/todo';

/** What the jobs list draws. */
interface BedJobsProps {
  /** The one to-do list (the brief's todo). */
  todo: BriefTodo;
  /** The bed's place in it: its jobs' keys, its part of each, "2 more jobs". */
  bed: BriefBed;
  /** Ticking, from the page's useTodoMarks. */
  marks: TodoMarks;
  /** Draw only the first so many, then "2 more jobs" linking to the open bed; all when absent. */
  limit?: number;
}

/**
 * The bed's jobs, flat, a rule between them, each as this bed's part of it.
 *
 * @param props - BedJobsProps.
 */
export const BedJobs: React.FC<BedJobsProps> = ({ todo, bed, marks, limit }) => {
  const byKey = React.useMemo(() => new Map(todo.jobs.map((job) => [job.key, job])), [todo.jobs]);
  const jobs = bed.keys.map((key) => byKey.get(key)).filter((job): job is TodoJob => job !== undefined);
  const shown = limit === undefined ? jobs : jobs.slice(0, limit);
  if (shown.length === 0) return null;
  /**
   * The job as this bed shows it: its plants here, done when they are.
   *
   * @param job - The whole job.
   */
  const here = (job: TodoJob): TodoJob => {
    const view = bed.views[job.key];
    // This bed's part of a shared job: its title, reason, plants (with this bed's note alone) and done (the audit, 2026-10-16).
    return view ? {
      ...job, title: view.title ?? job.title, reason: view.reason !== undefined ? view.reason : job.reason, plants: view.plants, plantIds: view.plantIds, done: view.done,
    } : job;
  };
  /**
   * A mark from this bed's card: the whole circle of a job shared with other beds ticks only this bed's plants.
   *
   * @param job - The whole job.
   * @param mark - What the card asked.
   */
  const markHere = (job: TodoJob, mark: TodoMark): void => {
    const view = bed.views[job.key];
    const whole = (mark.action === 'done' || mark.action === 'undone') && !mark.plantIds;
    void marks.mark(job, view && whole && job.perPlant ? { ...mark, plantIds: view.plantIds } : mark);
  };
  return (
    <>
      {/* Folds a job away and opens a new one smoothly (AnimatedList; owner, 2026-10-16). */}
      <AnimatedList
        className="divide-y divide-border"
        itemClassName="-mx-2"
        items={shown.map((job) => ({
          key: job.key,
          node: (
            <JobCard
              job={here(job)}
              words={todo.words}
              pending={marks.pending[job.key]}
              busy={marks.busyKey === job.key}
              failedWords={marks.failed?.key === job.key ? marks.failed.words : null}
              flat
              onMark={(mark) => markHere(job, mark)}
            />
          ),
        }))}
      />
      {limit !== undefined && bed.more && (
        <Link to={`/beds/${bed.id}`} className="inline-flex min-h-9 items-center text-sm font-semibold text-primary hover:underline">
          {bed.more}
        </Link>
      )}
    </>
  );
};
